import type { ApiError, Envelope } from './types'

/**
 * API 客户端（技术方案 §5.1）：
 *  - 身份：登录换 JWT（HS256，SR 独立密钥）后所有请求带 Authorization: Bearer
 *  - 写请求自动生成 Idempotency-Key，并在重试时复用同一个键（幂等重放）
 *  - 并发控制统一走 If-Match: "<row_version>"
 *  - 回传 trace_id，便于用户把 P0 事件交给运营
 */
const BASE = '/api/v1'
const TOKEN_KEY = 'sr.token'

export interface Session {
  principalId: string
  orgId: string
  roles: string[]
  sessionId: string
  idleExpiresAt?: number
  expiresAt: number
}

let token: string | null = window.localStorage.getItem(TOKEN_KEY)
let session: Session | null = token ? decodeSession(token) : null

/** 只在浏览器侧读 JWT 载荷用于显隐菜单；**权限裁决始终在服务端**（§6.1）。 */
function decodeSession(jwt: string): Session | null {
  const parts = jwt.split('.')
  if (parts.length !== 3) return null
  try {
    const payload = JSON.parse(atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>
    const sub = String(payload.sub ?? '')
    const orgId = String(payload.org_id ?? '')
    if (sub === '' || orgId === '') return null
    return {
      sessionId: String(payload.sid ?? ''),
      principalId: sub,
      orgId,
      roles: Array.isArray(payload.roles) ? payload.roles.map(String) : [],
      expiresAt: Number(payload.exp ?? 0) * 1000,
    }
  } catch {
    return null
  }
}

export function currentSession(): Session | null {
  if (session && session.expiresAt > 0 && session.expiresAt < Date.now()) {
    setToken(null)
    return null
  }
  return session
}

export function setToken(next: string | null): void {
  token = next
  if (next === null) {
    window.localStorage.removeItem(TOKEN_KEY)
    session = null
  } else {
    window.localStorage.setItem(TOKEN_KEY, next)
    session = decodeSession(next)
  }
}

export function clearSession(): void { setToken(null) }

export class KbError extends Error {
  code: string
  status: number
  traceId: string
  retryable: boolean
  degrade: string
  retryAfterSeconds: number

  constructor(problem: ApiError) {
    super(problem.detail || problem.title || '请求失败')
    this.code = problem.code
    this.status = problem.status
    this.traceId = problem.trace_id
    this.retryable = problem.retryable
    this.degrade = problem.degrade
    this.retryAfterSeconds = problem.retry_after_seconds ?? 0
  }
}

function headers(extra?: Record<string, string>): Record<string, string> {
  const base: Record<string, string> = { Accept: 'application/json', ...extra }
  if (token) base.Authorization = `Bearer ${token}`
  return base
}

async function parse<T>(response: Response, requestToken: string | null = token, expireOn401 = true): Promise<T> {
  const text = await response.text()
  let body: unknown = {}
  if (text !== '') {
    try {
      body = JSON.parse(text) as unknown
    } catch {
      // 代理错误可能返回 HTML；保留 HTTP 状态和 trace，而不是暴露 JSON.parse 异常。
      body = {
        type: 'about:blank', title: response.statusText || '请求失败', status: response.status,
        detail: response.status === 429 ? '操作过于频繁，请稍后重试' : response.status >= 500 ? '服务暂时不可用，请稍后重试' : '请求未能完成，请刷新后重试（HTTP ' + response.status + '）', code: response.status === 401 ? 'AUTH_UNAUTHENTICATED' : 'HTTP_ERROR',
        trace_id: response.headers.get('x-trace-id') ?? '-',
        retryable: response.status >= 500 || response.status === 408 || response.status === 429, degrade: '—',
      } satisfies ApiError
    }
  }
  if (expireOn401 && response.status === 401 && requestToken === token) { clearSession(); window.dispatchEvent(new Event('kb:session-expired')) }
  if (!response.ok) {
    const problem = body as Partial<ApiError>
    throw new KbError({
      type: problem.type ?? 'about:blank', title: problem.title ?? '请求失败', status: problem.status ?? response.status,
      detail: problem.detail ?? `HTTP ${response.status}`, code: problem.code ?? 'HTTP_ERROR',
      trace_id: problem.trace_id ?? response.headers.get('x-trace-id') ?? '-',
      retryable: problem.retryable ?? (response.status >= 500 || response.status === 408 || response.status === 429),
      degrade: problem.degrade ?? '—',
      retry_after_seconds: problem.retry_after_seconds ?? Number(response.headers.get('retry-after') ?? (response.status === 429 ? 60 : 0)),
    })
  }
  return body as T
}

/** 登录使用服务端当前账号记录，凭据不保存在浏览器。 */
export async function login(principalId: string, password: string): Promise<Session> {
  const res = await fetch('/auth/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ principal_id: principalId, password }),
  })
  const body = await parse<{token:string;idle_expires_at?:string}>(res,null,false)
  setToken(body.token)
  if (!session) throw new Error('登录响应异常，请重试')
  session = {...session,idleExpiresAt:body.idle_expires_at ? Date.parse(body.idle_expires_at) : undefined}
  return session
}

export async function refreshSession(): Promise<{ session: Session; policy: Record<string, string[]> }> {
  const requestToken = token
  const result = await parse<{ roles: string[]; role_permissions: Record<string, string[]>; project_access?: boolean; idle_expires_at?: string }>(await fetch('/auth/session', { headers: headers(), cache: 'no-store' }), requestToken)
  if (!session || requestToken !== token) throw new Error('登录已失效')
  session = { ...session, roles: result.project_access ? [...result.roles, '__project_navigation'] : result.roles, idleExpiresAt: result.idle_expires_at ? Date.parse(result.idle_expires_at) : session.idleExpiresAt }
  return { session, policy: { ...result.role_permissions, __project_navigation: result.project_access ? ['project.view'] : [] } }
}

export function syncStoredSession(): Session | null {
  token = window.localStorage.getItem(TOKEN_KEY)
  session = token ? decodeSession(token) : null
  return currentSession()
}
export type AuthPolicy = { session_ttl_seconds: number; idle_timeout_seconds: number; failed_attempt_limit: number; lockout_seconds: number }
export async function authPolicy(): Promise<AuthPolicy> { return parse(await fetch('/auth/policy',{cache:'no-store'}),null,false) }
export async function reportActivity(): Promise<Session> {
  const requestToken=token
  const result=await parse<{idle_expires_at:string}>(await fetch('/auth/activity',{method:'POST',headers:headers(),cache:'no-store'}),requestToken)
  if(!session || token!==requestToken) throw new Error('登录已失效')
  session={...session,idleExpiresAt:Date.parse(result.idle_expires_at)}
  return session
}
export async function logout(): Promise<void> {
  const requestToken = token
  const response = await fetch('/auth/logout', {method:'POST',headers:headers(),cache:'no-store'})
  if (response.status !== 401) await parse(response,requestToken)
  if (token === requestToken) clearSession()
}
export async function changePassword(current: string, next: string, confirm: string): Promise<void> {
  const requestToken = token
  await parse(await fetch('/auth/password',{method:'POST',headers:headers({'Content-Type':'application/json'}),body:JSON.stringify({current_password:current,new_password:next,confirm_password:confirm})}),requestToken)
}

export async function apiGet<T>(path: string, query?: Record<string, string | number | undefined>, options?:{signal?:AbortSignal}) {
  const url = new URL(BASE + path, window.location.origin)
  Object.entries(query ?? {}).forEach(([k, v]) => {
    if (v !== undefined && v !== '') url.searchParams.set(k, String(v))
  })
  const requestToken = token
  return parse<T>(await fetch(url, { headers: headers(), cache: 'no-store',signal:options?.signal }), requestToken)
}

export async function apiSend<T>(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  options?: { expectedVersion?: number; idempotencyKey?: string },
) {
  const key = options?.idempotencyKey ?? crypto.randomUUID()
  const extra: Record<string, string> = { 'Idempotency-Key': key }
  if (body !== undefined) extra['Content-Type'] = 'application/json'
  if (options?.expectedVersion !== undefined) extra['If-Match'] = `"${options.expectedVersion}"`

  const requestToken = token
  const response = await fetch(BASE + path, {
    method,
    headers: headers(extra),
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  const result = await parse<T>(response, requestToken)
  if (requestToken === token && /^\/(assets|revisions|revision-reviews|asset-archive-requests)(\/|$)/.test(path)) {
    window.dispatchEvent(new Event('kb:reviews-changed'))
  }
  return result
}

export type TargetMarket = 'unspecified' | 'domestic' | 'overseas' | 'both' | 'general'
/** Authenticated upload with actual transport progress; URLs/tokens are never
 * supplied by the editor. The server returns an ordinary envelope. */
export function apiUpload<T>(path:string,body:unknown,onProgress:(percent:number)=>void,options:{idempotencyKey:string;expectedVersion:number;signal:AbortSignal}):Promise<T>{
 const requestToken=token
 return new Promise((resolve,reject)=>{
  const request=new XMLHttpRequest(),abort=()=>request.abort(),clean=()=>options.signal.removeEventListener('abort',abort)
  if(options.signal.aborted){reject(new DOMException('上传已取消','AbortError'));return}
  request.open('POST',BASE+path);request.timeout=90000
  Object.entries(headers({'Content-Type':'application/json','Idempotency-Key':options.idempotencyKey,'If-Match':`"${options.expectedVersion}"`})).forEach(([name,value])=>request.setRequestHeader(name,value))
  request.upload.onprogress=event=>{if(event.lengthComputable)onProgress(Math.min(100,Math.round(event.loaded/event.total*100)))}
  request.onload=()=>{clean();if(request.status===413){reject(new Error('图片请求过大，请缩小后重试'));return}void parse<T>(new Response(request.responseText,{status:request.status,headers:{'Content-Type':request.getResponseHeader('Content-Type')??'application/json'}}),requestToken).then(resolve,reject)}
  request.onerror=()=>{clean();reject(new Error('图片上传失败，请检查网络后重试'))};request.ontimeout=()=>{clean();reject(new Error('图片上传超时，请重试'))};request.onabort=()=>{clean();reject(new DOMException('图片上传已取消','AbortError'))}
  options.signal.addEventListener('abort',abort,{once:true});request.send(JSON.stringify(body))
 })
}
export type MarketClarification = {kind:'target_market';question:string;message:string;options:{value:TargetMarket;label:string}[]}
export type KbStreamMeta = {
  trace_id?: string
  answer_id?: string
  answer_status?: string
  citations?: Array<Record<string, unknown>>
  target_market?: TargetMarket
  clarification?: MarketClarification
}

export type KbStreamDone = { answer_id?: string; model?: string; answer_status?: string; continuable?: boolean; validation?:{status:string} }

/** 读取 /kb/answers 的 SSE；普通 apiSend JSON 合约保持不变。 */
export async function streamKbAnswer(
  question: string,
  handlers: {
    onMeta?: (meta: KbStreamMeta) => void
    onDelta?: (text: string) => void
    onProgress?: (progress: { part: number; message: string }) => void
    onDone?: (done: KbStreamDone) => void
    onError?: (error: { code?: string; detail?: string }) => void
  },
  signal?: AbortSignal,
  history: { role: string; text: string; incomplete?: boolean }[] = [],
  targetMarket?: TargetMarket,
  imageDataUrl?:string,
  contextAnswerId?:string,
): Promise<void> {
  const requestToken = token
  const response = await fetch(BASE + '/kb/answers', {
    method: 'POST',
    headers: headers({ 'Accept': 'text/event-stream', 'Content-Type': 'application/json', 'Idempotency-Key': crypto.randomUUID() }),
    body: JSON.stringify({ question, scope: { kind: 'library', ref: null }, locale: 'zh-CN', history, ...(targetMarket ? {target_market:targetMarket} : {}),...(imageDataUrl?{image_data_url:imageDataUrl}:{}),...(contextAnswerId?{context_answer_id:contextAnswerId}:{}) }),
    signal,
  })
  if (!response.ok) await parse(response,requestToken)
  if (!response.body) throw new Error('浏览器不支持流式响应')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let event = ''
  let data = ''
  let finished = false
  const dispatch = () => {
    if (event === '' || data === '') { event = ''; data = ''; return }
    let payload: unknown
    try { payload = JSON.parse(data) } catch { event = ''; data = ''; return }
    if (event === 'meta') handlers.onMeta?.(payload as KbStreamMeta)
    else if (event === 'delta') handlers.onDelta?.(String((payload as { text?: unknown }).text ?? ''))
    else if (event === 'progress') handlers.onProgress?.(payload as { part: number; message: string })
    else if (event === 'done') { finished = true; handlers.onDone?.(payload as KbStreamDone) }
    else if (event === 'error') handlers.onError?.(payload as { code?: string; detail?: string })
    event = ''; data = ''
  }
  while (true) {
    const part = await reader.read()
    buffer += decoder.decode(part.value, { stream: !part.done })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''
    for (const raw of lines) {
      const line = raw.replace(/\r$/, '')
      if (line === '') { dispatch(); continue }
      if (line.startsWith('event:')) event = line.slice(6).trim()
      else if (line.startsWith('data:')) data += line.slice(5).trim()
    }
    if (part.done) break
  }
  if (buffer !== '') {
    const line = buffer.replace(/\r$/, '')
    if (line.startsWith('event:')) event = line.slice(6).trim()
    else if (line.startsWith('data:')) data += line.slice(5).trim()
  }
  dispatch()
  if (!finished && !signal?.aborted) throw new Error('生成连接已中断，已保留收到的内容，可点击继续生成。')
}

export type AsyncJobSnapshot = {
  id: string
  status: string
  result?: Record<string, unknown> | null
  error?: string | null
  progress?: number
}

/**
 * 轮询任务状态（202 受理后按任务接口查询，不得用业务详情接口代偿）。
 * PPT 解析包含下载、soffice 转换、逐页渲染和差异计算，大文件超过 2 分钟是正常情况；
 * 默认等待 15 分钟。短暂网关错误只退避重试，不把“已上传”的文件误报成失败。
 */
export async function waitForJob(
  jobId: string,
  timeoutMs = 15 * 60 * 1000,
  intervalMs = 1500,
  onStatus?: (job: AsyncJobSnapshot) => void,
): Promise<AsyncJobSnapshot> {
  const deadline = Date.now() + timeoutMs
  let transientFailures = 0
  while (Date.now() < deadline) {
    try {
      const result = await apiGet<Envelope<AsyncJobSnapshot[]>>('/operations/tasks', { job_id: jobId, limit: 1 })
      const job = (result.data ?? []).find((item) => item.id === jobId)
      if (job) {
        onStatus?.(job)
        if (['succeeded', 'failed', 'cancelled', 'dead_letter'].includes(job.status)) return job
      }
      transientFailures = 0
      await new Promise((resolve) => setTimeout(resolve, intervalMs))
    } catch (error) {
      // 发布滚动重载或短暂网络抖动期间，Nginx 可能返回 502/503/504。
      // 任务本身仍在 Worker 中执行，不能把一次可恢复的查询失败当成上传失败。
      const retryable = error instanceof KbError ? error.retryable : error instanceof TypeError
      if (!retryable) throw error
      transientFailures += 1
      const backoff = Math.min(intervalMs * Math.max(1, transientFailures), 5000)
      await new Promise((resolve) => setTimeout(resolve, backoff))
    }
  }
  const error = new Error('文件已上传，解析仍在后台进行，请稍后从任务中心继续查看')
  error.name = 'AsyncJobTimeoutError'
  throw error
}
